import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import '../../../styles/StrategyRoom.css';
import { useStrategyLogic } from '../hooks/useStrategyLogic';
import StrategyCanvas from '../components/canvas/StrategyCanvas';
import BottomControls from '../components/bottom_panel/BottomControls';

import TopBar from '../../../components/TopBar';
import SaveStrategyModal from '../../../components/SaveStrategyModal';
import TitleSetter from '../../../components/TitleSetter';
import GuidedTour from '../../../components/GuidedTour';
import { useGuidedTour } from '../../../hooks/useGuidedTour';
import { isPlayerRole } from '../../../utils/teamRole';

const StrategyRoomView = (props) => {
  const [showLandscapePrompt, setShowLandscapePrompt] = useState(false);
  const { strategyId } = useParams();
  const {
    state,
    actions,
    refs,
  } = useStrategyLogic({ strategyId, ...props });

  const {
    // State
    showDropdown,
    isConfirmSaveModalOpen,
    communityAssetPacks,
    seasonAssets,
    seasonPlayers,
    assetsLoading,
    customPlayers,
    teamId,
    teamAssetsExpanded,
    teamAssetsSide,
    hoveredPlayerMenu,
    strategyTitle,
    editingStrategyId,
    selectedObject,
    pendingMapName,
    isLoadedFromFirestore,
    loadedStepsLocked,
    stepSwitching,
    saveNotification,
    isSaveModalOpen,
    currentUser,
    userProfile,
    teamLogo,
    cursorPos,
    mode,
    mapName,
    isMapCollapsed,
    showAssignmentsOverlay,
    draggingUtilityId,
    isPenPanelVisible,
    activeStep,
    justLoaded,
    playersOnCanvas,
    utilitiesOnCanvas,
    textBoxes,
    drawnLines,
    imagesOnCanvas,
    visionCones,
    selectedConeType,
    canvasDimensions,
    draggingPlayerId,
    isErasing,
    draggingTextId,
    activeTool,
    penSettings,
    lastTool,
    isDrawing,
    isCursorInside,
    expandedTeam,
    scenarioData,
    MODES,
    modeLabels,
    modeMaps,
    selectedMap,
    currentRoster,
    primaryIconStyle,
    secondaryIconStyle,
    penTypes,
    colors,
    assignmentRows,
    roleCounts,
  } = state;

  // Guided Tour
  const tour = useGuidedTour(teamId);

  useEffect(() => {
    const checkOrientationPrompt = () => {
      const hasTouchInput =
        window.matchMedia('(pointer: coarse)').matches ||
        navigator.maxTouchPoints > 0;
      const isPhoneOrTabletViewport = window.matchMedia('(max-width: 1180px)').matches;
      const isPortrait =
        window.matchMedia('(orientation: portrait)').matches ||
        window.innerHeight > window.innerWidth;

      setShowLandscapePrompt(Boolean(hasTouchInput && isPhoneOrTabletViewport && isPortrait));
    };

    checkOrientationPrompt();
    window.addEventListener('resize', checkOrientationPrompt);
    window.addEventListener('orientationchange', checkOrientationPrompt);

    return () => {
      window.removeEventListener('resize', checkOrientationPrompt);
      window.removeEventListener('orientationchange', checkOrientationPrompt);
    };
  }, []);

  useEffect(() => {
    const setAppVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
    };

    setAppVh();
    window.addEventListener('resize', setAppVh);
    window.addEventListener('orientationchange', setAppVh);

    return () => {
      window.removeEventListener('resize', setAppVh);
      window.removeEventListener('orientationchange', setAppVh);
    };
  }, []);

  const {
    // Actions
    setShowDropdown,
    setIsConfirmSaveModalOpen,
    setCustomPlayers,
    setAssetsLoading,
    setTeamId,
    setTeamAssetsExpanded,
    setTeamAssetsSide,
    setHoveredPlayerMenu,
    setStrategyTitle,
    setEditingStrategyId,
    setSelectedObject,
    setPendingMapName,
    setIsLoadedFromFirestore,
    setLoadedStepsLocked,
    setStepSwitching,
    setSaveNotification,
    setIsSaveModalOpen,
    setCurrentUser,
    setUserProfile,
    setTeamLogo,
    setCursorPos,
    setMode,
    setMapName,
    setIsMapCollapsed,
    setShowAssignmentsOverlay,
    setDraggingUtilityId,
    setIsPenPanelVisible,
    setActiveStep,
    setJustLoaded,
    setPlayersOnCanvas,
    setUtilitiesOnCanvas,
    setTextBoxes,
    setDrawnLines,
    setImagesOnCanvas,
    setVisionCones,
    setSelectedConeType,
    setCanvasDimensions,
    setDraggingPlayerId,
    setIsErasing,
    setDraggingTextId,
    setActiveTool,
    setPenSettings,
    setLastTool,
    setIsDrawing,
    setIsCursorInside,
    setExpandedTeam,
    setScenarioData,
    undo,
    redo,
    saveAllSequencesToFirestore,
    handleUpdateStrategy,
    switchMode,
    updateTextBox,
    initResize,
    handleImageUpload,
    observeTextboxResize,
    clearEverything,
    deletePlayers,
    deleteUtilities,
    deleteTextBoxes,
    deleteDrawings,
    deleteCurrentSequenceStep,
    getMousePosition,
    eraseNearbyLines,
    handleMouseLeave,
    handleUIHover,
    toggleTool,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleCanvasClick,
    getPlayerNameByAvatar,
    generateUniqueId,
    updateAssignment,
    handleAssignDragStart,
    // Step switching
    handleStepSwitch,
    // Canvas handlers
    onCanvasClick,
    onDrop,
    onPlayerClick,
    onPlayerMouseDown,
    onUtilityClick,
    onUtilityMouseDown,
    onTextBoxClick,
    onTextBoxMouseDown,
    onTextBoxInput,
    onImageClick,
    onImageMouseDown,
    onConesChange,
    onMouseEnter,
  } = actions;

  const {
    // Refs
    canvasLoadedRef,
    hoverMenuTimeout,
    suspendHistoryRef,
    dragJustEndedRef,
    historyRef,
    resizeObservers,
    penPanelRef,
    canvasSize,
    dragOffset,
    cursorRef,
    pathRef,
    pointsRef,
    eraseThrottleRef,
    lastEraserPointRef,
    resizingRef,
    assignPanelPos,
  } = refs;

  const canSaveAsNewStrategy = !isPlayerRole(userProfile?.role);

  return (
    <>
      <TitleSetter title="MyStrategy" />
      {showLandscapePrompt && (
        <div className="landscape-prompt-overlay" role="status" aria-live="polite">
          <div className="landscape-prompt-card">
            <h3>Rotate your device</h3>
            <p>For the best strategy-canvas view, use landscape mode.</p>
          </div>
        </div>
      )}
      <div className="container">
        <TopBar
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          teamLogo={teamLogo}
          setTeamLogo={setTeamLogo}
        />
        {/* Main Content */}
        <div className="main-content">
          {/* Sidebar */}
          <div className="sidebar" onContextMenu={(e) => e.preventDefault()}>
            <div className="mode-switcher">
              {MODES.map((m) => (
                <button
                  key={m}
                  className={`mode-button ${mode === m ? 'active' : ''}`}
                  onClick={() => switchMode(m)}
                >
                  {modeLabels[m]}
                </button>
              ))}
            </div>

            <div className={`map-list-wrapper ${isMapCollapsed ? 'collapsed' : 'expanded'}`}>
              <div className="map-list">
                {(isMapCollapsed ? modeMaps.filter((m) => m.name === mapName) : modeMaps).map((map) => (
                  <div
                    key={map.name}
                    className={`map-card ${map.name === mapName ? 'selected lifted' : ''}`}
                    onClick={() => {
                      if (isMapCollapsed) setIsMapCollapsed(false);
                      else {
                        setMapName(map.name);
                        setIsMapCollapsed(true);
                      }
                    }}
                    style={{ backgroundImage: `url(${map.thumbnail || map.image})` }}
                  >
                    <div className="map-label">{map.name.toUpperCase()}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sequence Selector */}
            <div className="sequence-selector">
              <h2 className="sequence-title">Sequence</h2>
              <div className="sequence-grid">
                {Array.from({ length: 10 }, (_, i) => {
                  const step = i + 1;
                  return (
                    <button
                      key={step}
                      className={`sequence-btn ${activeStep === step ? 'active' : ''}`}
                      onClick={() => handleStepSwitch(step)}
                    >
                      {step}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* DELETE PANEL */}
            <div className="delete-panel">
              <h3 className="delete-title">Delete</h3>
              <button className="delete-btn full" onClick={clearEverything}>
                Everything
              </button>
              <button className="delete-btn" onClick={deleteCurrentSequenceStep}>
                Sequence Step {activeStep}
              </button>
              <div className="delete-options">
                <button className="delete-icon" onClick={deletePlayers}>
                  <img src="/assets/removePlayer.png" alt="removePlayer" className="delete-icon-image" />
                </button>
                <button className="delete-icon" onClick={deleteUtilities}>
                  <img src="/assets/noUtils.png" alt="noUtils" className="delete-icon-image" />
                </button>
                <button className="delete-icon" onClick={deleteDrawings}>
                  <img src="/assets/removeLines.png" alt="removeLines" className="delete-icon-image" />
                </button>
                <button className="delete-icon" onClick={deleteTextBoxes}>
                  <img src="/assets/removeTextbox.png" alt="removeTextbox" className="delete-icon-image" />
                </button>
                <button className="delete-icon" disabled>
                  <img src="/assets/removePic.png" alt="removePic" className="delete-icon-image" />
                </button>
              </div>
            </div>

            {/* TOOL PANEL */}
            <div className="tools-panel">
              <h3 className="tools-title">Tools</h3>
              <div
                className="tools-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(56px, 1fr))',
                  gridAutoRows: '42px',
                  columnGap: '12px',
                  rowGap: '6px',
                  justifyItems: 'stretch',
                  width: '100%',
                }}
              >
                <div className="tool-dropdown-group">
                  <button
                    className={`tool tool-icon-button ${activeTool === 'pen' ? 'active is-active' : ''}`}
                    onClick={() => toggleTool('pen')}
                    style={{ height: '52px' }}
                  >
                    <img src="/assets/pencil-icon.png" alt="Pen" className="tool-icon" style={primaryIconStyle} />
                  </button>

                  {activeTool === 'pen' && isPenPanelVisible && (
                    <div
                      className="pen-floating-panel pen-toolbar-dropdown"
                      id="penPanel"
                      ref={penPanelRef}
                    >
                      <div className="panel-header">
                        <h4>Pen Settings</h4>
                        <button
                          className="close-btn"
                          onClick={() => {
                            setIsPenPanelVisible(false);
                            setActiveTool(null);
                          }}
                        >
                          ×
                        </button>
                      </div>

                      <div className="pen-section">
                        <label>Type:</label>
                        <div className="type-grid">
                          {penTypes.map((type) => (
                            <button
                              key={type.id}
                              className={penSettings.type === type.id ? 'selected' : ''}
                              onClick={() => setPenSettings({ ...penSettings, type: type.id })}
                            >
                              {type.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="pen-section">
                        <label>Color:</label>
                        <div className="color-palette">
                          {colors.map((c) => (
                            <div
                              key={c}
                              className="color-dot"
                              style={{
                                backgroundColor: c,
                                border: penSettings.color === c ? '2px solid white' : '1px solid gray',
                              }}
                              onClick={() => setPenSettings({ ...penSettings, color: c })}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="pen-section sliders">
                        <label>Opacity: {penSettings.opacity}</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={penSettings.opacity}
                          onChange={(e) => setPenSettings({ ...penSettings, opacity: parseInt(e.target.value, 10) })}
                        />
                      </div>

                      <div className="pen-section sliders">
                        <label>Thickness: {penSettings.thickness}</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={penSettings.thickness}
                          onChange={(e) => setPenSettings({ ...penSettings, thickness: parseInt(e.target.value, 10) })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <button
                  className={`tool tool-icon-button ${activeTool === 'eraser' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('eraser')}
                  style={{ height: '52px' }}
                >
                  <img src="/assets/eraser-icon.png" alt="eraser" className="tool-icon" style={primaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button ${activeTool === 'text' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('text')}
                  style={{ height: '52px' }}
                >
                  <img src="/assets/text.png" alt="text" className="tool-icon" style={primaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button ${activeTool === 'image' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('image')}
                  style={{ height: '52px' }}
                >
                  <img src="/assets/addimage.png" alt="image" className="tool-icon" style={primaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${activeTool === 'sndbomb' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('sndbomb')}
                  style={{ height: '40px' }}
                  title="SND Bomb"
                >
                  <img src="/assets/sndbomb.png" alt="sndbomb" className="tool-icon" style={{ ...secondaryIconStyle, filter: 'none' }} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${selectedConeType === 'wide' ? 'active is-active' : ''}`}
                  onClick={() => {
                    setSelectedConeType('wide');
                    toggleTool('visionCone', 'wide');
                  }}
                  style={{ height: '40px' }}
                  title="Vision Cone - Wide"
                >
                  <img src="/assets/tools/Untitled-3.png" alt="Wide Vision Cone" className="tool-icon" style={{ ...secondaryIconStyle, filter: 'none' }} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${selectedConeType === 'medium' ? 'active is-active' : ''}`}
                  onClick={() => {
                    setSelectedConeType('medium');
                    toggleTool('visionCone', 'medium');
                  }}
                  style={{ height: '40px' }}
                  title="Vision Cone - Medium"
                >
                  <img src="/assets/tools/Untitled-2.png" alt="Medium Vision Cone" className="tool-icon" style={{ ...secondaryIconStyle, filter: 'none' }} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${selectedConeType === 'narrow' ? 'active is-active' : ''}`}
                  onClick={() => {
                    setSelectedConeType('narrow');
                    toggleTool('visionCone', 'narrow');
                  }}
                  style={{ height: '40px' }}
                  title="Vision Cone - Narrow"
                >
                  <img src="/assets/tools/Untitled-1.png" alt="Narrow Vision Cone" className="tool-icon" style={{ ...secondaryIconStyle, filter: 'none' }} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${activeTool === 'warning' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('warning')}
                  style={{ height: '40px' }}
                  title="Warning"
                >
                  <img src="/assets/tools/WARNING.png" alt="Warning" className="tool-icon" style={secondaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${activeTool === 'anchor' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('anchor')}
                  style={{ height: '40px' }}
                  title="Anchor"
                >
                  <img src="/assets/tools/ANCHOR.png" alt="Anchor" className="tool-icon" style={secondaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${activeTool === 'flag' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('flag')}
                  style={{ height: '40px' }}
                  title="Flag"
                >
                  <img src="/assets/tools/FLAG.png" alt="Flag" className="tool-icon" style={secondaryIconStyle} />
                </button>

                <button
                  className={`tool tool-icon-button secondary-tool ${activeTool === 'star' ? 'active is-active' : ''}`}
                  onClick={() => toggleTool('star')}
                  style={{ height: '40px' }}
                  title="Star"
                >
                  <img src="/assets/tools/STAR.png" alt="Star" className="tool-icon" style={secondaryIconStyle} />
                </button>
              </div>

            </div>
          </div>

          {/* Canvas */}
          <StrategyCanvas
            selectedMap={selectedMap}
            drawnLines={drawnLines}
            playersOnCanvas={playersOnCanvas}
            utilitiesOnCanvas={utilitiesOnCanvas}
            textBoxes={textBoxes}
            visionCones={visionCones}
            imagesOnCanvas={imagesOnCanvas}
            activeStep={activeStep}
            selectedObject={selectedObject}
            activeTool={activeTool}
            isCursorInside={isCursorInside}
            cursorPos={cursorPos}
            penSettings={penSettings}
            canvasDimensions={canvasDimensions}
            teamAssetsSide={teamAssetsSide}
            showAssignmentsOverlay={showAssignmentsOverlay}
            assignPanelPos={assignPanelPos}
            assignmentRows={assignmentRows}
            roleCounts={roleCounts}
            selectedConeType={selectedConeType}
            pathRef={pathRef}
            cursorRef={cursorRef}
            canvasSize={canvasSize}
            dragOffset={dragOffset}
            historyRef={historyRef}
            onCanvasClick={onCanvasClick}
            onDrop={onDrop}
            onPlayerClick={onPlayerClick}
            onPlayerMouseDown={onPlayerMouseDown}
            onUtilityClick={onUtilityClick}
            onUtilityMouseDown={onUtilityMouseDown}
            onTextBoxClick={onTextBoxClick}
            onTextBoxMouseDown={onTextBoxMouseDown}
            onTextBoxInput={onTextBoxInput}
            onImageClick={onImageClick}
            onImageMouseDown={onImageMouseDown}
            onInitResize={initResize}
            onImageUpload={handleImageUpload}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onMouseEnter={onMouseEnter}
            onConesChange={onConesChange}
            onAssignDragStart={handleAssignDragStart}
            updateAssignment={updateAssignment}
            generateUniqueId={generateUniqueId}
            handleCanvasClick={handleCanvasClick}
          />

          {/* Right Panel */}
          <div className="right-panel" onContextMenu={(e) => e.preventDefault()}>
            <h3 style={{ marginTop: 0 }}>Debug Tools</h3>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0 14px' }}>
              <input
                type="checkbox"
                checked={showAssignmentsOverlay}
                onChange={() => setShowAssignmentsOverlay(v => !v)}
              />
              <span>Show assignments overlay</span>
            </label>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '10px' }}>
              <button
                onClick={undo}
                style={{
                  padding: '10px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#7E57C2')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#444')}
              >
                Undo
              </button>

              <button
                onClick={redo}
                style={{
                  padding: '10px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  width: '100%',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#7E57C2')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#444')}
              >
                Redo
              </button>
            </div>

            <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '1px solid #333' }}>
              <h4 style={{ color: '#bbb', marginBottom: '8px' }}>Selected Object</h4>

              {selectedObject ? (
                <>
                  <p style={{ fontSize: '14px', margin: 0 }}>
                    Type:{' '}
                    {selectedObject.type.charAt(0).toUpperCase() + selectedObject.type.slice(1)}
                    {selectedObject.type === 'player' && (
                      <>
                        {' '}
                        [
                        {getPlayerNameByAvatar(
                          playersOnCanvas.find(p => p.id === selectedObject.id)?.avatar
                        )}
                        ]
                      </>
                    )}
                  </p>

                  <button
                    onClick={() => {
                      if (selectedObject.type === 'player') {
                        setPlayersOnCanvas(prev => prev.filter(p => p.id !== selectedObject.id));
                      } else if (selectedObject.type === 'utility') {
                        setUtilitiesOnCanvas(prev => prev.filter(u => u.id !== selectedObject.id));
                      } else if (selectedObject.type === 'text') {
                        setTextBoxes(prev => prev.filter(t => t.id !== selectedObject.id));
                      }
                      setSelectedObject(null);
                    }}
                    style={{
                      marginTop: '12px',
                      padding: '10px',
                      backgroundColor: '#e53935',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      width: '100%'
                    }}
                  >
                    ❌ Remove Selected
                  </button>
                </>
              ) : (
                <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
                  No object selected.
                </p>
              )}
            </div>

            <hr style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }} />

            {/* Save/Update Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {editingStrategyId && (
                <button
                  onClick={handleUpdateStrategy}
                  style={{
                    padding: '10px',
                    backgroundColor: '#388e3c',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    width: '100%',
                    fontWeight: 'bold'
                  }}
                >
                  Update Existing Strategy
                </button>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {canSaveAsNewStrategy && (
                  <button
                    onClick={() => setIsSaveModalOpen(true)}
                    style={{
                      padding: '10px',
                      backgroundColor: '#7E57C2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      width: '100%',
                      fontWeight: 'bold'
                    }}
                  >
                    Save as New Strategy
                  </button>
                )}

                {saveNotification && (
                  <div style={{
                    marginTop: '10px',
                    color: '#4CAF50',
                    backgroundColor: '#1e1e1e',
                    padding: '10px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}>
                    ✅ Strategy saved successfully!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Panel */}
        <BottomControls
          customPlayers={customPlayers}
          seasonPlayers={seasonPlayers}
          communityAssetPacks={communityAssetPacks}
          seasonLogo={seasonAssets?.logo}
          assetsLoading={assetsLoading}
          teamAssetsExpanded={teamAssetsExpanded}
          teamAssetsSide={teamAssetsSide}
          expandedTeam={expandedTeam}
          playersOnCanvas={playersOnCanvas}
          activeStep={activeStep}
          hoveredPlayerMenu={hoveredPlayerMenu}
          utilitiesOnCanvas={utilitiesOnCanvas}
          textBoxes={textBoxes}
          drawnLines={drawnLines}
          imagesOnCanvas={imagesOnCanvas}
          visionCones={visionCones}
          hoverMenuTimeout={hoverMenuTimeout}
          historyRef={historyRef}
          dragJustEndedRef={dragJustEndedRef}
          setTeamAssetsSide={setTeamAssetsSide}
          setTeamAssetsExpanded={setTeamAssetsExpanded}
          setExpandedTeam={setExpandedTeam}
          setPlayersOnCanvas={setPlayersOnCanvas}
          setActiveTool={setActiveTool}
          setIsPenPanelVisible={setIsPenPanelVisible}
          setHoveredPlayerMenu={setHoveredPlayerMenu}
          toggleTool={toggleTool}
          generateUniqueId={generateUniqueId}
        />
      </div>

      {/* Save Strategy Modal */}
      {isSaveModalOpen && canSaveAsNewStrategy && (
        <SaveStrategyModal
          map={selectedMap}
          mapName={mapName}
          mode={mode}
          user={userProfile}
          onCancel={() => setIsSaveModalOpen(false)}
          onConfirm={async (formData) => {
            const success = await saveAllSequencesToFirestore(formData, mode);
            if (success) {
              setSaveNotification(true);
              setTimeout(() => setSaveNotification(false), 10000);
            }
            return success;
          }}
        />
      )}

      {/* Utility hover menu portal */}
      {hoveredPlayerMenu &&
        createPortal(
          (() => {
            const { rect } = hoveredPlayerMenu;
            const top = Math.max(8, rect.top - 46);
            const left = rect.left + rect.width / 2;
            return (
              <div
                style={{
                  position: 'fixed',
                  top,
                  left,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '6px 8px',
                  background: '#0f172a',
                  borderRadius: '8px',
                  boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
                  zIndex: 5000,
                }}
                onMouseEnter={() => {
                  if (hoverMenuTimeout.current) clearTimeout(hoverMenuTimeout.current);
                }}
                onMouseLeave={() => {
                  if (hoverMenuTimeout.current) clearTimeout(hoverMenuTimeout.current);
                  hoverMenuTimeout.current = setTimeout(() => setHoveredPlayerMenu(null), 120);
                }}
              >
                {[
                  { key: 'grenade', icon: '/assets/grenade-icon.png' },
                  { key: 'trophy', icon: '/assets/trophy-icon.png' },
                  { key: 'smoke', icon: '/assets/smoke.png' },
                  { key: 'flash', icon: '/assets/flash.png' },
                  { key: 'empgrenade', icon: '/assets/empgrenade.png' },
                ].map((util) => (
                  <button
                    key={util.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTool(util.key);
                      setHoveredPlayerMenu(null);
                    }}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      border: 'none',
                      background: '#111827',
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                      padding: 8,
                    }}
                  >
                    <img src={util.icon} alt={util.key} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                  </button>
                ))}
              </div>
            );
          })(),
          document.body
        )}

      <ToastContainer />
      
      {/* Guided Tour */}
      {teamId && (
        <GuidedTour
          isActive={tour.showTour}
          onComplete={tour.completeTour}
          onSkip={tour.skipTour}
        />
      )}
    </>
  );
};

export default StrategyRoomView;

